import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Plus, RefreshCw, Rocket, Settings2, Star, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Count, EmptyState, Page, PageHeader, TableCard, Toolbar } from '@/components/shell'
import { api, type SupernovaCampaignRow, type SupernovaSettings } from '@/lib/api'
import { formatDateTime } from '@/lib/format'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

import {
  CAMPAIGN_ID_PATTERN,
  ENTRY_SWITCH_HINT,
  ENTRY_SWITCH_LABEL,
  PHASE_META,
  QueryError,
  RESULT_STATUS_META,
  ReasonField,
  SAMPLE_INTERVALS,
  SAMPLE_INTERVAL_HINT,
  SAMPLE_INTERVAL_LABEL,
  entryMeta,
  errorMessage,
  fromLocalInputValue,
  toLocalInputValue,
  reasonValid,
  useConfirm,
  type PendingAction,
} from './-shared'

export const Route = createFileRoute('/dashboard/supernova/')({
  component: SupernovaCampaignsPage,
})

type CampaignList = { data: SupernovaCampaignRow[] }
type SettingsResponse = { data: SupernovaSettings }

function SupernovaCampaignsPage() {
  const canManage = useCan()('supernova', 'manage')
  const confirm = useConfirm()
  const [creating, setCreating] = useState<{ mode: 'new' | 'clone'; sourceId?: string } | null>(
    null,
  )

  const query = useQuery({
    queryKey: ['supernova-campaigns'],
    queryFn: () => api.get<CampaignList>('/supernova/campaigns'),
    retry: false,
    refetchInterval: 60_000,
  })

  /**
   * 全局设置与活动列表**分开两条查询**。设置读不到时（IGNIX 不可达）活动列表照常出：
   * 报名进度与阶段是运营日常要盯的数字，为了两个开关把整页打成错误页不成比例。
   */
  const settings = useQuery({
    queryKey: ['supernova-settings'],
    queryFn: () => api.get<SettingsResponse>('/supernova/settings'),
    retry: false,
    refetchInterval: 60_000,
  })

  const rows = query.data?.data ?? []

  return (
    <Page>
      <PageHeader
        title="SuperNova"
        description="与 METAGENTS.AI 合办活动的运营控制台。一期活动一行，可多期并存；发奖不在本后台。"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="pressable rounded-apple-sm"
              disabled={query.isFetching}
              onClick={() => void query.refetch()}
            >
              <RefreshCw className={cn('size-3.5', query.isFetching && 'animate-spin')} />
              刷新
            </Button>
            {canManage && (
              <Button
                size="sm"
                className="pressable rounded-apple-sm"
                onClick={() => setCreating({ mode: 'new' })}
              >
                <Plus className="size-3.5" />
                新建活动
              </Button>
            )}
          </div>
        }
      />

      <SettingsSection
        settings={settings.data?.data}
        isPending={settings.isPending}
        error={settings.isError ? settings.error : null}
        onRetry={() => void settings.refetch()}
        canManage={canManage}
        ask={confirm.ask}
      />

      {query.isError ? (
        <QueryError
          title="读不到 SuperNova 活动列表"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <section className="space-y-4">
          <Toolbar trailing={<Count value={rows.length} unit="期" />}>
            <p className="text-[12px] text-mut2">
              「当前期」是前台展示的那一期，同一时刻只能有一期；在活动详情页切换。
              入口总开关是全局的，在上面那一块里改。
            </p>
          </Toolbar>

          <TableCard>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-line/50 hover:bg-transparent">
                    <TableHead className="section-label pl-6">活动</TableHead>
                    <TableHead className="section-label">阶段</TableHead>
                    <TableHead className="section-label">起止时间</TableHead>
                    <TableHead className="section-label">报名</TableHead>
                    <TableHead className="section-label">结果状态</TableHead>
                    <TableHead className="section-label">当前期</TableHead>
                    {canManage && (
                      <TableHead className="section-label pr-6 text-right">操作</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {query.isPending &&
                    Array.from({ length: 4 }, (_, index) => (
                      <TableRow key={index} className="border-line/40">
                        <TableCell colSpan={canManage ? 7 : 6} className="px-6 py-2.5">
                          <Skeleton className="h-7 w-full rounded-apple-sm" />
                        </TableCell>
                      </TableRow>
                    ))}

                  {query.isSuccess && rows.length === 0 && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={canManage ? 7 : 6}>
                        <EmptyState
                          icon={Rocket}
                          title="还没有任何活动"
                          description={
                            canManage
                              ? '点右上角「新建活动」开一期。新建出来的活动不是当前期，配置好再设为当前期即可通过链接访问；需要在导航显示入口时，再打开上面的开关。'
                              : '等管理员建好第一期活动后，这里会列出全部期次。'
                          }
                        />
                      </TableCell>
                    </TableRow>
                  )}

                  {rows.map((row) => {
                    const phaseMeta = PHASE_META[row.phase] ?? PHASE_META.unconfigured!
                    const resultMeta = RESULT_STATUS_META[row.resultStatus]
                    return (
                      <TableRow key={row.id} className="row-hover border-line/40">
                        <TableCell className="pl-6">
                          <Link
                            to="/dashboard/supernova/$id"
                            params={{ id: row.id }}
                            className="block min-w-0 transition-colors hover:text-cyan"
                          >
                            <div className="truncate text-[13px] font-medium text-txt">
                              {row.name}
                            </div>
                            <div className="truncate font-mono text-[10.5px] text-dim">
                              {row.id}
                            </div>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn('h-5 text-[10.5px]', phaseMeta.className)}>
                            {phaseMeta.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[11.5px] text-mut">
                          <div>{formatDateTime(row.startAt)}</div>
                          <div className="text-dim">{formatDateTime(row.endAt)}</div>
                        </TableCell>
                        <TableCell className="text-[12.5px] text-txt">
                          <span className="tnum font-mono">{row.registrations.registered}</span>
                          {row.registrations.disqualified > 0 && (
                            <span className="ml-1.5 tnum font-mono text-[10.5px] text-rose">
                              -{row.registrations.disqualified}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={cn(
                              'h-5 text-[10.5px]',
                              resultMeta?.className ?? 'bg-edge/10 text-mut hover:bg-edge/10',
                            )}
                          >
                            {resultMeta?.label ?? row.resultStatus}
                          </Badge>
                        </TableCell>
                        {/*
                          入口开关是**全局**的，不挂在行上 —— 这一列只说「哪一期在展示」。
                          早先每行各挂一枚「入口已开 / 已关」徽章，那会让人以为能按期开关。
                        */}
                        <TableCell>
                          {row.isCurrent ? (
                            <Badge
                              className="h-5 gap-1 bg-cyan/15 text-[10.5px] text-cyan hover:bg-cyan/15"
                              title="入口打开时前台展示的就是这一期"
                            >
                              <Star className="size-3" />
                              当前期
                            </Badge>
                          ) : (
                            <span className="text-[11.5px] text-dim">—</span>
                          )}
                        </TableCell>
                        {canManage && (
                          <TableCell className="pr-6 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              className="pressable rounded-apple-sm"
                              onClick={() => setCreating({ mode: 'clone', sourceId: row.id })}
                            >
                              复制为新一期
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </TableCard>
        </section>
      )}

      <CreateDialog
        state={creating}
        onClose={() => setCreating(null)}
        existingIds={rows.map((row) => row.id)}
      />

      {confirm.dialog}
    </Page>
  )
}

/**
 * 全局设置。**两项都是全站级的**，不挂在任何一期活动上 —— 所以它在活动列表的上方，
 * 而不是某一期的详情页里。
 *
 * 摆在这一页顶部而不是另开一页：运营来这里的动作序列是「看一眼哪期在跑 → 开/关入口」，
 * 拆成两页会让最常做的那件事多一次跳转。
 *
 * 读设置失败时**不接管整页**：活动列表仍然要能看（报名进度与阶段是日常要盯的数字），
 * 这一块自己退化成一条带重试的提示。
 */
function SettingsSection({
  settings,
  isPending,
  error,
  onRetry,
  canManage,
  ask,
}: {
  settings: SupernovaSettings | undefined
  isPending: boolean
  error: unknown
  onRetry: () => void
  canManage: boolean
  ask: (action: PendingAction) => void
}) {
  /** 改完刷的是设置自己那条查询 —— 活动列表不受影响，没必要跟着重取。 */
  const invalidate = ['supernova-settings']

  const intervals = settings?.allowedSampleIntervals ?? SAMPLE_INTERVALS

  return (
    <section className="surface space-y-4 p-6">
      <div className="flex items-center gap-2.5">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <Settings2 className="size-4 text-cyan" />
        </div>
        <div className="min-w-0">
          <h2 className="text-[15px] font-medium tracking-tight text-txt">全局设置</h2>
          <p className="mt-0.5 text-[12px] text-mut2">
            全站级，三期活动共用。改动立刻生效，与任何一期活动的阶段无关。
          </p>
        </div>
        {settings && (
          <Badge
            className={cn(
              'ml-auto h-6 shrink-0 text-[11px]',
              entryMeta(settings.entryVisible).className,
            )}
          >
            {entryMeta(settings.entryVisible).label}
          </Badge>
        )}
      </div>

      {isPending && <Skeleton className="h-28 w-full rounded-apple" />}

      {error != null && (
        <div className="flex items-start gap-2 rounded-apple bg-warn/[0.07] px-3.5 py-2.5 text-[11.5px] leading-relaxed text-warn">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span className="min-w-0 flex-1">读不到全局设置：{errorMessage(error)}</span>
          <Button
            variant="outline"
            size="sm"
            className="pressable h-7 shrink-0 rounded-apple-sm"
            onClick={onRetry}
          >
            重试
          </Button>
        </div>
      )}

      {settings && (
        <div className="space-y-2.5">
          <div className="flex items-start justify-between gap-4 rounded-apple border border-line/60 p-3">
            <div className="min-w-0">
              <div className="text-[12.5px] font-medium text-txt">{ENTRY_SWITCH_LABEL}</div>
              <p className="mt-0.5 text-[11px] leading-relaxed text-mut2">{ENTRY_SWITCH_HINT}</p>
            </div>
            <Switch
              aria-label={ENTRY_SWITCH_LABEL}
              checked={settings.entryVisible}
              disabled={!canManage}
              onCheckedChange={(entryVisible) =>
                ask({
                  title: entryVisible ? '打开前台入口？' : '关闭前台入口？',
                  description: entryVisible
                    ? '打开后顶部导航会显示 X Campaign 入口。活动页展示标为「当前期」的那一期；没有当前期时显示无活动提示。'
                    : '关闭后仅隐藏顶部导航入口，仍可通过 /x_campaign 直接访问当前期活动。活动本身照常运行：报名、快照与榜单都不受影响，已报名的数据也不动。',
                  confirmLabel: entryVisible ? '确认打开入口' : '确认关闭入口',
                  destructive: !entryVisible,
                  successMessage: entryVisible ? '前台入口已打开' : '前台入口已关闭',
                  invalidate,
                  run: (reason) => api.put('/supernova/settings', { entryVisible, reason }),
                })
              }
            />
          </div>

          <div className="flex items-start justify-between gap-4 rounded-apple border border-line/60 p-3">
            <div className="min-w-0">
              <div className="text-[12.5px] font-medium text-txt">
                {SAMPLE_INTERVAL_LABEL}（分钟）
              </div>
              <p className="mt-0.5 text-[11px] leading-relaxed text-mut2">{SAMPLE_INTERVAL_HINT}</p>
            </div>
            <Select
              value={String(settings.sampleIntervalMin)}
              disabled={!canManage}
              onValueChange={(value) => {
                const sampleIntervalMin = Number(value)
                if (sampleIntervalMin === settings.sampleIntervalMin) return
                ask({
                  title: '修改价格采样间隔？',
                  description: `采样间隔从 ${settings.sampleIntervalMin} 分钟改为 ${sampleIntervalMin} 分钟，下一轮采样起生效，三期活动同时受影响。改小会让市值窗口里的样本变多，改大则变少 —— 已配好的「市值样本数」可能因此超过窗口能提供的上限。`,
                  confirmLabel: '确认修改',
                  successMessage: `采样间隔已改为 ${sampleIntervalMin} 分钟`,
                  invalidate,
                  run: (reason) => api.put('/supernova/settings', { sampleIntervalMin, reason }),
                })
              }}
            >
              <SelectTrigger
                className="h-9 w-32 shrink-0 rounded-apple text-[13px]"
                aria-label={SAMPLE_INTERVAL_LABEL}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-apple">
                {intervals.map((value) => (
                  <SelectItem key={value} value={String(value)}>
                    {value} 分钟
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <HackathonSchedule
            key={`${settings.hackathonStartAt}:${settings.hackathonEndAt}`}
            settings={settings}
            canManage={canManage}
            ask={ask}
          />

          <p className="text-[11px] text-mut2">
            最近修改：{formatDateTime(settings.updatedAt)} · {settings.updatedBy ?? '未知账号'}
          </p>
        </div>
      )}

      {settings && !canManage && (
        <p className="text-[11.5px] text-mut2">只读视图。修改全局设置需要 SuperNova 管理权限。</p>
      )}
    </section>
  )
}

function HackathonSchedule({
  settings,
  canManage,
  ask,
}: {
  settings: SupernovaSettings
  canManage: boolean
  ask: (action: PendingAction) => void
}) {
  const [start, setStart] = useState(toLocalInputValue(settings.hackathonStartAt))
  const [end, setEnd] = useState(toLocalInputValue(settings.hackathonEndAt))
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const startAt = fromLocalInputValue(start)
  const endAt = fromLocalInputValue(end)
  const valid =
    (!start && !end) ||
    (startAt !== null && endAt !== null && Date.parse(endAt) > Date.parse(startAt))
  const changed =
    start !== toLocalInputValue(settings.hackathonStartAt) ||
    end !== toLocalInputValue(settings.hackathonEndAt)

  return (
    <div className="space-y-3 rounded-apple border border-line/60 p-3">
      <div className="text-[12.5px] font-medium text-txt">黑客松活动时间</div>
      <p className="text-[11px] leading-relaxed text-mut2">
        独立于 Supernova 各期活动，控制黑客松倒计时、日期与规则说明。按当前浏览器时区 {timezone}{' '}
        输入；访客按各自时区查看。结束时间为精确截止时刻。
      </p>
      <div className="flex flex-wrap gap-3">
        <label className="space-y-1 text-[12px]">
          黑客松开始时间
          <Input
            type="datetime-local"
            value={start}
            disabled={!canManage}
            onChange={(event) => setStart(event.target.value)}
          />
        </label>
        <label className="space-y-1 text-[12px]">
          黑客松结束时间
          <Input
            type="datetime-local"
            value={end}
            disabled={!canManage}
            onChange={(event) => setEnd(event.target.value)}
          />
        </label>
      </div>
      {!valid && (
        <p role="alert" className="text-[11px] text-warn">
          请成对填写开始和结束时间，且结束时间须晚于开始时间。
        </p>
      )}
      <p className="text-[11px] text-mut2">两项留空并保存即可清空配置，前台显示时间待定。</p>
      <Button
        size="sm"
        disabled={!canManage || !valid || !changed}
        onClick={() =>
          ask({
            title: '修改黑客松活动时间？',
            description:
              startAt && endAt
                ? `黑客松时间将调整为 ${formatDateTime(startAt)} 至 ${formatDateTime(endAt)}（${timezone}）。`
                : '清空黑客松时间配置，前台将显示时间待定。',
            confirmLabel: '确认保存时间',
            successMessage: '黑客松活动时间已保存',
            invalidate: ['supernova-settings'],
            run: (reason) =>
              api.put('/supernova/settings', {
                hackathonStartAt: startAt,
                hackathonEndAt: endAt,
                reason,
              }),
          })
        }
      >
        保存黑客松时间
      </Button>
    </div>
  )
}

/**
 * 新建 / 复制。两件事共用一个弹窗——它们要填的东西完全一样（新 slug + 名称 + 理由），
 * 只有提交的地址和那句解释不同。
 */
function CreateDialog({
  state,
  onClose,
  existingIds,
}: {
  state: { mode: 'new' | 'clone'; sourceId?: string } | null
  onClose: () => void
  existingIds: string[]
}) {
  const queryClient = useQueryClient()
  const [id, setId] = useState('')
  const [name, setName] = useState('')
  const [startAt, setStartAt] = useState('')
  const [endAt, setEndAt] = useState('')
  const [reason, setReason] = useState('')

  const isClone = state?.mode === 'clone'

  const reset = () => {
    setId('')
    setName('')
    setStartAt('')
    setEndAt('')
    setReason('')
  }

  const submit = useMutation({
    mutationFn: () => {
      const trimmedName = name.trim()
      if (!isClone) {
        return api.post('/supernova/campaigns', {
          id: id.trim(),
          name: trimmedName,
          reason: reason.trim(),
        })
      }

      /**
       * 空字段一律**不发这个键**，而不是发一个空串。
       *
       * IGNIX 靠「这个键不在」决定兜底行为：名称兜成「原名 副本」、时间窗留空让这一期
       * 落在 unconfigured。发空串会变成一个无名活动，发 null 会被 400 挡下。
       */
      return api.post(`/supernova/campaigns/${state!.sourceId}/clone`, {
        id: id.trim(),
        ...(trimmedName ? { name: trimmedName } : {}),
        ...(fromLocalInputValue(startAt) ? { startAt: fromLocalInputValue(startAt) } : {}),
        ...(fromLocalInputValue(endAt) ? { endAt: fromLocalInputValue(endAt) } : {}),
        reason: reason.trim(),
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['supernova-campaigns'] })
      toast.success(isClone ? '已复制为新一期' : '活动已创建', { description: id.trim() })
      reset()
      onClose()
    },
    onError: (error) => toast.error('创建失败', { description: errorMessage(error) }),
  })

  const trimmedId = id.trim()
  // 撞 id 在服务端会被 409 挡下，但那要等一次往返；列表就在手上，先在本地说一声。
  const idTaken = existingIds.includes(trimmedId)
  const idOk = CAMPAIGN_ID_PATTERN.test(trimmedId) && !idTaken

  const startIso = fromLocalInputValue(startAt)
  const endIso = fromLocalInputValue(endAt)
  const rangeValid = startIso === null || endIso === null || new Date(startIso) < new Date(endIso)

  // 复制时名称可省 —— IGNIX 会命名成「原名 副本」。新建必须自己起名。
  const nameOk = isClone || name.trim().length > 0
  const canSubmit = idOk && nameOk && rangeValid && reasonValid(reason)

  return (
    <AlertDialog
      open={state !== null}
      onOpenChange={(open) => {
        if (!open && !submit.isPending) {
          reset()
          onClose()
        }
      }}
    >
      <AlertDialogContent className="rounded-apple-xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[17px] text-txt">
            {isClone ? '复制为新一期？' : '新建活动'}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[12.5px] leading-relaxed text-mut">
            {isClone ? (
              <>
                复制 <span className="font-mono text-txt">{state?.sourceId}</span>{' '}
                的配置开新一期。起止时间、入口开关与结果状态都不复制 —— 下面不填时间的话，
                新一期落在「未配置」。
              </>
            ) : (
              '按最新默认规则新建活动：9 档阶梯奖池，交易量 / 市值 / 盈利分配 40% / 40% / 20%。起止时间需另行配置。'
            )}
            <span className="mt-2 block">
              新活动<b>不是当前期</b>，要之后在详情页单独设。前台入口是全局开关， 不随新建活动改变。
            </span>
            <span className="mt-1 block">
              报名与快照另有前提 —— 起止时间没配好之前，那两个接口一律拒绝。
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <label htmlFor="supernova-new-id" className="text-[12.5px] font-medium">
              活动 ID
            </label>
            <Input
              id="supernova-new-id"
              value={id}
              placeholder="supernova-2026-09"
              disabled={submit.isPending}
              aria-invalid={trimmedId.length > 0 && !idOk}
              className="h-9 rounded-apple font-mono text-[13px]"
              onChange={(event) => setId(event.target.value)}
            />
            <p className="text-[11px] text-mut2">
              {idTaken
                ? '这个 ID 已经被占用，换一个。'
                : '小写字母、数字与连字符，3–64 位。建好之后不能改——它是前台链接的一部分。'}
            </p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="supernova-new-name" className="text-[12.5px] font-medium">
              活动名称{isClone && <span className="ml-1 text-[11px] text-mut2">（可留空）</span>}
            </label>
            <Input
              id="supernova-new-name"
              value={name}
              placeholder={isClone ? '留空则命名为「原名 副本」' : 'SuperNova 2026 秋季赛'}
              disabled={submit.isPending}
              className="h-9 rounded-apple text-[13px]"
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          {isClone && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor="supernova-new-start" className="text-[12.5px] font-medium">
                  开始时间<span className="ml-1 text-[11px] text-mut2">（可留空）</span>
                </label>
                <Input
                  id="supernova-new-start"
                  type="datetime-local"
                  value={startAt}
                  disabled={submit.isPending}
                  className="h-9 rounded-apple text-[13px]"
                  onChange={(event) => setStartAt(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="supernova-new-end" className="text-[12.5px] font-medium">
                  结束时间<span className="ml-1 text-[11px] text-mut2">（可留空）</span>
                </label>
                <Input
                  id="supernova-new-end"
                  type="datetime-local"
                  value={endAt}
                  disabled={submit.isPending}
                  aria-invalid={!rangeValid}
                  className="h-9 rounded-apple text-[13px]"
                  onChange={(event) => setEndAt(event.target.value)}
                />
              </div>
              {!rangeValid && (
                <p className="text-[11.5px] text-rose sm:col-span-2">结束时间必须晚于开始时间。</p>
              )}
            </div>
          )}

          <ReasonField
            id="supernova-new-reason"
            value={reason}
            onChange={setReason}
            disabled={submit.isPending}
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-apple" disabled={submit.isPending}>
            返回检查
          </AlertDialogCancel>
          <Button
            className="pressable rounded-apple"
            disabled={!canSubmit || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? '提交中…' : '确认创建'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
